import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Music, Check, X, Loader2, AlertTriangle, ShieldCheck, RefreshCw, ExternalLink } from 'lucide-react';
import { validateCoApprovalLink, decideCoApproval, setCoApprovalAlerts } from '../../services/coApproval';
import { getRequestsByEventId, type SongRequest } from '../../services/requests';
import { getSavedSongAnalysis, type SavedSongAnalysis } from '../../services/songReviews';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FirebaseError } from 'firebase/app';
import { isAlreadyHandledError, shareLinkErrorMessage } from '../../utils/shareLinks';
import { isOnCoApproverList } from '../../utils/coApprovalRouting';
import { playerLinkFor } from '../../utils/playerLinks';
import type { PlayerProvider } from '../../services/playModes';

const LISTEN_BUTTONS: { provider: PlayerProvider; label: string }[] = [
  { provider: 'apple', label: 'Apple Music' },
  { provider: 'spotify', label: 'Spotify' },
];

export default function CoApprovalPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [eventName, setEventName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Rejection modal
  const [rejectingRequest, setRejectingRequest] = useState<SongRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // AI analysis already saved for each song (null = none saved); the co-approver cannot run one
  const [savedAnalyses, setSavedAnalyses] = useState<Map<string, SavedSongAnalysis | null>>(new Map());
  const lookedUpRequestIdsRef = useRef<Set<string>>(new Set());

  // Alert emails
  const [hasEmail, setHasEmail] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [alertsSaving, setAlertsSaving] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!eventId || !token) {
      setError('Invalid co-approval link');
      setLoading(false);
      return;
    }

    const validate = async () => {
      try {
        const linkState = await validateCoApprovalLink(eventId, token);

        // A deleted event's links stop working (ITER-154)
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (!eventDoc.exists() || eventDoc.data().deletedAt != null) {
          setError('Co-approval session not found or has been revoked');
          return;
        }
        setSessionId(linkState.sessionId);
        setHasEmail(linkState.hasEmail);
        setAlertsEnabled(linkState.alertsEnabled);
        setEventName(eventDoc.data().name || 'Event');
      } catch (validateError) {
        console.error('Co-approval link validation failed', validateError);
        if (validateError instanceof FirebaseError && validateError.code === 'functions/permission-denied') {
          setError('Co-approval session not found or has been revoked');
        } else {
          setError('Failed to connect');
        }
      } finally {
        setLoading(false);
      }
    };

    validate();
  }, [eventId, token]);

  const loadSavedAnalyses = useCallback(async (waiting: SongRequest[]) => {
    const notLookedUp = waiting.filter(request => !lookedUpRequestIdsRef.current.has(request.id));
    if (notLookedUp.length === 0) return;
    notLookedUp.forEach(request => lookedUpRequestIdsRef.current.add(request.id));

    const lookedUp = await Promise.all(
      notLookedUp.map(async (request) => {
        try {
          return [request.id, await getSavedSongAnalysis(request.song.title, request.song.artist)] as const;
        } catch (lookupError) {
          console.warn('Saved song analysis lookup failed', lookupError);
          return [request.id, null] as const;
        }
      })
    );
    setSavedAnalyses(prev => {
      const next = new Map(prev);
      lookedUp.forEach(([requestId, analysis]) => next.set(requestId, analysis));
      return next;
    });
  }, []);

  // Fetch pending co-approval requests
  const fetchRequests = useCallback(async () => {
    if (!eventId || !sessionId) return;
    try {
      const all = await getRequestsByEventId(eventId);
      const waiting = all.filter(isOnCoApproverList);
      setRequests(waiting);
      await loadSavedAnalyses(waiting);
    } catch (fetchError) {
      console.warn('Co-approval request fetch failed, retrying on the next poll', fetchError);
    }
  }, [eventId, sessionId, loadSavedAnalyses]);

  // Poll for requests
  useEffect(() => {
    if (!sessionId) return;
    fetchRequests();
    const interval = setInterval(fetchRequests, 4000);
    return () => clearInterval(interval);
  }, [sessionId, fetchRequests]);

  const handleDecisionError = (decisionError: unknown) => {
    console.error('Co-approval decision failed', decisionError);
    toast.error(shareLinkErrorMessage(decisionError, 'co-approval'));
    if (isAlreadyHandledError(decisionError)) {
      fetchRequests();
    }
  };

  const handleApprove = async (requestId: string) => {
    if (!eventId || !token) return;
    setProcessingId(requestId);
    try {
      await decideCoApproval({ eventId, token, requestId, decision: 'approve' });
      setRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (approveError) {
      handleDecisionError(approveError);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectingRequest || !eventId || !token) return;
    setProcessingId(rejectingRequest.id);
    try {
      await decideCoApproval({
        eventId,
        token,
        requestId: rejectingRequest.id,
        decision: 'reject',
        reason: rejectionReason,
      });
      setRequests(prev => prev.filter(r => r.id !== rejectingRequest.id));
      setRejectingRequest(null);
      setRejectionReason('');
    } catch (rejectError) {
      handleDecisionError(rejectError);
    } finally {
      setProcessingId(null);
    }
  };

  const handleToggleAlerts = async (enabled: boolean) => {
    if (!eventId || !token) return;
    const previous = alertsEnabled;
    setAlertsEnabled(enabled);
    setAlertsSaving(true);
    try {
      setAlertsEnabled(await setCoApprovalAlerts(eventId, token, enabled));
    } catch (alertsError) {
      console.error('Co-approval alert switch failed', alertsError);
      setAlertsEnabled(previous);
      toast.error(shareLinkErrorMessage(alertsError, 'co-approval'));
    } finally {
      setAlertsSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRequests();
    setRefreshing(false);
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-base-content/60">Connecting...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-base-content/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-b border-blue-500/30 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-blue-500" />
          <div>
            <h1 className="text-lg font-bold">Co-Approval</h1>
            <p className="text-sm text-base-content/60">{eventName}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm font-medium text-blue-500">{requests.length} pending</span>
            <button
              onClick={handleRefresh}
              className="btn btn-ghost btn-sm btn-circle"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        {hasEmail && (
          <label className="max-w-lg mx-auto mt-3 flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={alertsEnabled}
              disabled={alertsSaving}
              onChange={e => handleToggleAlerts(e.target.checked)}
            />
            Email me when songs are waiting
          </label>
        )}
      </div>

      {/* Request List */}
      <div className="max-w-lg mx-auto p-4 space-y-3">
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <Music className="w-12 h-12 text-base-content/20 mx-auto mb-3" />
            <p className="text-base-content/60 font-medium">No requests awaiting approval</p>
            <p className="text-sm text-base-content/40 mt-1">
              New requests will appear here automatically.
            </p>
          </div>
        ) : (
          requests.map(request => {
            const analysis = savedAnalyses.get(request.id);
            return (
              <div
                key={request.id}
                className="rounded-lg border bg-base-200/50 border-base-300 p-4"
              >
                <div className="flex items-start gap-3">
                  {/* Album Art */}
                  {request.song.albumArtUrl ? (
                    <img
                      src={request.song.albumArtUrl}
                      alt=""
                      className="w-14 h-14 rounded-md object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-md bg-base-300 flex items-center justify-center flex-shrink-0">
                      <Music className="w-6 h-6 text-base-content/30" />
                    </div>
                  )}

                  {/* Song Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{request.song.title}</p>
                    <p className="text-sm text-base-content/60 truncate">{request.song.artist}</p>
                    {request.prizeSkipTheLine ? (
                      <>
                        <span className="inline-block text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded mt-1">
                          🏆 Prize pick
                        </span>
                        <p className="text-xs text-base-content/50 mt-1">
                          A game winner chose this. Approving puts it at the top of the queue.
                        </p>
                      </>
                    ) : request.superRequest ? (
                      <>
                        <span className="inline-block text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded mt-1">
                          👑 People's Pick
                        </span>
                        <p className="text-xs text-base-content/50 mt-1">
                          Approving adds it to the People's Pick pool, where the crowd can vote it into the queue.
                        </p>
                      </>
                    ) : request.isOverflow ? (
                      <p className="text-xs text-base-content/50 mt-1">Approving adds it to the overflow pool.</p>
                    ) : null}
                    {request.message && (
                      <p className="text-xs text-base-content/50 mt-1 italic">"{request.message}"</p>
                    )}
                    {request.requestedBy.nickname && (
                      <p className="text-xs text-base-content/40 mt-0.5">
                        by {request.requestedBy.nickname}
                      </p>
                    )}
                    {request.reviewedBy && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                        Reviewed by {request.reviewedBy.name} (co-approval DJ)
                      </p>
                    )}
                    {request.song.explicitFlag && (
                      <span className="inline-block text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded mt-1">
                        Explicit
                      </span>
                    )}
                  </div>
                </div>

                {/* Listen Links */}
                <div className="mt-3 flex items-center gap-2">
                  {request.localFile ? (
                    <p className="text-xs text-base-content/50">Local file: no listen link</p>
                  ) : (
                    LISTEN_BUTTONS.map(({ provider, label }) => {
                      const link = playerLinkFor(request, provider);
                      return (
                        <a
                          key={provider}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline btn-xs gap-1"
                          title={link.isDirect ? 'Opens this song' : 'Searches for this song'}
                        >
                          <ExternalLink className="w-3 h-3" />
                          {label}
                        </a>
                      );
                    })
                  )}
                </div>

                {/* Saved AI Summary */}
                {analysis && (
                  <div className={`mt-3 text-xs p-2 rounded-lg ${
                    analysis.hasProfanity
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  }`}>
                    <p className="font-medium">
                      AI summary: {analysis.hasProfanity ? `⚠️ Profanity: ${analysis.profanityLevel}` : '✓ Clean'}
                    </p>
                    <p className="text-[11px] text-base-content/50 mb-1">Written by AI. May contain mistakes.</p>
                    <p className="text-base-content/70">{analysis.summary}</p>
                    {analysis.hasProfanity && (
                      <p className="mt-1 text-base-content/60">{analysis.profanityAnalysis}</p>
                    )}
                  </div>
                )}
                {analysis === null && (
                  <p className="mt-3 text-xs text-base-content/50">No AI summary was saved for this song.</p>
                )}

                {/* DJ Notes */}
                {request.djNotes && (
                  <div className="mt-2 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 p-2 rounded">
                    DJ note: {request.djNotes}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleApprove(request.id)}
                    disabled={processingId === request.id}
                    className="btn btn-sm flex-1 bg-green-500 hover:bg-green-600 text-white border-none"
                  >
                    {processingId === request.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Approve
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setRejectingRequest(request);
                      setRejectionReason('');
                    }}
                    disabled={processingId === request.id}
                    className="btn btn-sm flex-1 bg-red-500 hover:bg-red-600 text-white border-none"
                  >
                    <X className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Rejection Modal */}
      {rejectingRequest && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !processingId && setRejectingRequest(null)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-sm mx-auto bg-base-100 rounded-xl p-5 shadow-xl">
            <h3 className="text-lg font-bold mb-1">Reject Song</h3>
            <p className="text-sm text-base-content/60 mb-3">
              {rejectingRequest.song.title} — {rejectingRequest.song.artist}
            </p>
            <textarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection (optional)..."
              className="textarea textarea-bordered w-full mb-3"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                disabled={!!processingId}
                className="btn btn-sm flex-1 bg-red-500 hover:bg-red-600 text-white border-none"
              >
                {processingId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject'}
              </button>
              <button
                onClick={() => setRejectingRequest(null)}
                disabled={!!processingId}
                className="btn btn-sm btn-ghost flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
